// Ranks: the Leaderboards (a menu of boards, top 100 and your own rank) and
// the Records board (best catch of each species, as caught).
// Both come from the server on request: { t: 'leaderboards', board } and { t: 'records' }.
// The server rebuilds boards on a timer; a board is asked for again only once
// its next rebuild is due. Nothing here polls.
import { SPECIES, DEX_SPECIES } from '../shared/species.ts';
import { REGIONS } from '../shared/grounds.ts';
import { regionSpecies } from '../shared/bestiary.ts';
import { BOARD_IDS, PODIUM, isBoardId, monthLabel, type BoardId, type PodiumDTO } from '../shared/leaderboards.ts';
import { stepBoss } from '../shared/ladder.ts';
import { WEEK_CATCH, stepReward, weekCatchCount } from '../shared/weekcatch.ts';
import { weekOf } from '../shared/recipes.ts';
import { ITEM } from '../shared/items.ts';
import type { BoardRow, ClientMsg, CreatureDTO, LeaderboardsDTO, RecordDTO } from '../shared/protocol.ts';
import { S } from './state.ts';
import { spriteUrl, has, uiIcon } from './assets.ts';
import { esc, full, num, big } from './fmt.ts';
import { rollPills } from './rollpills.ts';
import { gradeBadge } from './badges.ts';
import { route } from './router.ts';
import { ICON } from './icons.ts';
import { playerIcon } from './playericon.ts';
import { tagChip } from './guild.ts';
import { playerLink } from './social.ts';
import { withSampleRecords, withSampleBoard, isSample, sampleName, SAMPLE_TAG } from './sample.ts';

export const C = {
  records: null as RecordDTO[] | null,
  boards: {} as Partial<Record<BoardId, LeaderboardsDTO>>,
  /** Records filters. */
  q: '',
  region: '',
  mine: false,
  /** Level board: total XP shown under each level (a tap on any level toggles it; a mouse shows it on hover). */
  showXp: false,
};

let send: (m: ClientMsg) => void = () => {};
let rerender: () => void = () => {};
export function bindCommunity(o: { send: (m: ClientMsg) => void; render: () => void }) { send = o.send; rerender = o.render; }

const boardOf = (): BoardId => { const s = route().sub; return isBoardId(s) ? s : 'level'; };

/** Ask for a board unless the copy we hold is still the server's latest build. */
function askBoard(id: BoardId, force = false) {
  const have = C.boards[id];
  if (force || !have || Date.now() >= have.next) send({ t: 'leaderboards', board: id });
}

/** Called when a Ranks page opens (sub: the board, or a species to find on Records), and by Refresh. */
export function loadCommunity(page: string, sub = '', force = false) {
  if (page === 'records') {
    if (sub && SPECIES[sub]) { C.q = SPECIES[sub].name; C.region = SPECIES[sub].region; C.mine = false; }
    send({ t: 'records' });
  }
  if (page === 'leaderboards') askBoard(isBoardId(sub) ? sub : 'level', force);
}

export function onRecords(list: RecordDTO[]) { C.records = list; rerender(); }
export function onBoards(data: LeaderboardsDTO) { C.boards[data.id] = data; rerender(); }

const BOARD_NAME: Record<BoardId, string> = {
  level: 'Level', dex: 'Bestiary', best: 'Best catch', auras: 'Auras', week: 'Weekly Catch', ladder: 'Boss Ladder', arena: 'Arena', guild: 'Guilds',
};
/** Each board's icon in the board menu. */
const BOARD_ICON: Record<BoardId, string> = {
  level: 'ui-ranks', dex: 'ui-bestiary', best: 'ui-showcase', auras: 'icon-aura', week: 'icon-gold-seal', ladder: 'ui-bosses', arena: 'ui-arena', guild: 'ui-guild',
};
/** The value column's heading. */
const VALUE_NAME: Record<BoardId, string> = {
  level: 'Level', dex: 'Species', best: 'Score', auras: 'Auras', week: 'Species', ladder: 'Step this month', arena: 'AR', guild: 'Power',
};

let dexTotal = 0;
const bestiaryTotal = () => dexTotal ||= new Set(REGIONS.flatMap((r) => regionSpecies(r.id))).size;

export function communityHead() {
  const p = S.page;
  const mine = S.trainer?.name;
  const recs = p === 'records' ? shownRecords() : null;
  const meta = recs
    ? `${full(recs.length)} / ${full(DEX_SPECIES.length)} species · ${full(recs.filter((r) => r.name === mine).length)} yours`
    : '';
  return {
    meta,
    tabs: `<nav class="subtabs"><a href="#/leaderboards/${boardOf()}" class="${p === 'leaderboards' ? 'on' : ''}">Leaderboards</a><a href="#/records" class="${p === 'records' ? 'on' : ''}">Records</a></nav>`,
    actions: `<button class="btn small" data-act="community-refresh">Refresh</button>`,
  };
}

/** A record as a creature, for the roll pills (level and power aren't part of a record). */
function asCreature(r: RecordDTO): CreatureDTO {
  return { id: 0, species: r.species, level: 1, xp: 0, rolls: r.rolls, stars: 0, relic: null, caughtAt: r.at, ground: '', bound: false, listed: false };
}

const day = (ts: number) => new Date(ts).toLocaleDateString([], { year: 'numeric', month: 'short', day: 'numeric' });

/** Records as shown: the server's, plus sample rows when an admin turned Sample data on (client/sample.ts). */
const shownRecords = () => withSampleRecords(C.records);
const holder = (r: RecordDTO) => (isSample(r) ? sampleName(r.name) : playerLink(r.name, `<b>${esc(r.name)}</b>`));
const agoShort = (ts: number) => { const d = Math.floor((Date.now() - ts) / 86_400_000); return d < 1 ? 'today' : d < 2 ? 'yesterday' : d < 60 ? `${d} days ago` : day(ts); };

/** One species on the board: its art, the record's grade and score, who holds it and since when; or an open slot. */
function recordCard(species: string, r: RecordDTO | undefined, me: string | undefined): string {
  const sp = SPECIES[species];
  const name = esc(sp?.name ?? species);
  if (!r) return `<div class="rc-card open" data-key="rc-${species}"><a class="rc-art" href="#/bestiary/${species}" title="${name} · Bestiary"><img src="${spriteUrl(species)}" alt=""></a>
    <div class="rc-body"><a class="rc-nm" href="#/bestiary/${species}">${name}</a><span class="rc-open">No record yet</span></div></div>`;
  const mine = r.name === me;
  // A held record opens the creature itself (read-only when it is someone else's); a sample row has none.
  const view = isSample(r) ? '' : ` data-cview="record:${species}" tabindex="0" title="View the record ${name}"`;
  return `<div class="rc-card${mine ? ' me' : ''}${r.rolls.aura ? ' aura' : ''}${isSample(r) ? ' smp' : ' cview'}" data-key="rc-${species}"${view}>
    <span class="rc-art"><img src="${spriteUrl(species, r.rolls.aura)}" alt=""></span>
    <div class="rc-body"><div class="rc-top"><a class="rc-nm" href="#/bestiary/${species}">${name}</a><span class="rc-score">${gradeBadge(r.score, 'sm')}<b>${r.score.toFixed(1)}</b></span></div>
      <div class="rc-who">${holder(r)}${mine ? '<span class="pill">You</span>' : ''}<span class="rc-at" title="${esc(day(r.at))}">${agoShort(r.at)}</span></div>
      ${rollPills(asCreature(r), { rollsOnly: true })}</div></div>`;
}

export function recordsPane(): string {
  const records = shownRecords();
  if (!records) return '<p class="hint">Loading…</p>';
  const me = S.trainer?.name;
  const q = C.q.trim().toLowerCase();
  const by = new Map(records.map((r) => [r.species, r]));
  // Who holds the most, and the newest records: the board's headline.
  const counts = new Map<string, { n: number; sample: boolean }>();
  for (const r of records) { const c = counts.get(r.name) ?? { n: 0, sample: isSample(r) }; c.n++; counts.set(r.name, c); }
  const leaders = [...counts].sort((a, b) => b[1].n - a[1].n || a[0].localeCompare(b[0])).slice(0, 5);
  const most = leaders[0]?.[1].n ?? 1;
  const lead = leaders.length ? `<section class="panel rc-lead"><h4>Most records</h4><ol>${leaders.map(([n, c], i) => `<li${n === me ? ' class="me"' : ''}><span class="rc-rank">${i + 1}</span>${c.sample ? sampleName(n) : playerLink(n, `<b>${esc(n)}</b>`)}<span class="rc-bar"><i style="width:${Math.round((c.n / most) * 100)}%"></i></span><b class="rc-n">${full(c.n)}</b></li>`).join('')}</ol></section>` : '';
  const newest = [...records].sort((a, b) => b.at - a.at).slice(0, 5);
  const fresh = newest.length ? `<section class="panel rc-fresh"><h4>Newest</h4><ul>${newest.map((r) => `<li><img src="${spriteUrl(r.species, r.rolls.aura)}" alt=""><span class="rc-fn"><a href="#/bestiary/${r.species}">${esc(SPECIES[r.species]?.name ?? r.species)}</a>${gradeBadge(r.score, 'sm')}</span><span class="rc-fw">${holder(r)}<span class="rc-at">${agoShort(r.at)}</span></span></li>`).join('')}</ul></section>` : '';
  const filters = `<div class="fbar rc-bar-f">
    <label class="fsearch">${ICON.search}<input data-rec="q" placeholder="Species or trainer" value="${esc(C.q)}" aria-label="Search records by species or trainer"></label>
    <button class="btn small tog${C.mine ? ' on' : ''}" data-act="rec-mine" aria-pressed="${C.mine}">Yours</button>
  </div>`;
  const match = (id: string) => {
    const r = by.get(id), name = (SPECIES[id]?.name ?? id).toLowerCase();
    if (C.mine && r?.name !== me) return false;
    return !q || name.includes(q) || !!r?.name.toLowerCase().includes(q);
  };
  // One region at a time under tabs (plus the top holders), its cards paged to fit: the page never scrolls.
  const regions = REGIONS.map((g) => {
    const ids = DEX_SPECIES.filter((id) => SPECIES[id].region === g.id);
    // Held records first (best score first), open species after, in Bestiary order.
    const shown = ids.filter(match).sort((a, b) => Number(by.has(b)) - Number(by.has(a)) || (by.get(b)?.score ?? 0) - (by.get(a)?.score ?? 0));
    return { g, ids, shown, held: ids.filter((id) => by.has(id)).length };
  });
  const filtered = !!q || C.mine;
  let tab = C.region === 'top' || regions.some((x) => x.g.id === C.region) ? C.region : regions[0].g.id;
  // A search that finds nothing here but something in another region opens that region.
  const cur = regions.find((x) => x.g.id === tab);
  if (filtered && cur && !cur.shown.length) tab = regions.find((x) => x.shown.length)?.g.id ?? tab;
  const tabs = `<nav class="psec-tabs all rc-tabs" style="--n:${regions.length + 1}">${regions.map((x) => `<button class="${x.g.id === tab ? 'on' : ''}" data-act="rec-region" data-id="${x.g.id}" aria-pressed="${x.g.id === tab}">${esc(x.g.name)} <span class="muted">${filtered ? full(x.shown.length) : `${full(x.held)}/${full(x.ids.length)}`}</span></button>`).join('')}<button class="${tab === 'top' ? 'on' : ''}" data-act="rec-region" data-id="top" aria-pressed="${tab === 'top'}">Top holders</button></nav>`;
  const open = regions.find((x) => x.g.id === tab);
  const body = tab === 'top'
    ? `<div class="rc-head">${lead}${fresh}</div>`
    : `${filters}${open?.shown.length ? `<div class="panel rc-grid" data-fit="rc-${tab}">${open.shown.map((id) => recordCard(id, by.get(id), me)).join('')}</div>` : `<p class="hint">${records.length ? 'No match.' : 'No records yet.'}</p>`}`;
  return `${tabs}${body}`;
}

/** The value cell of a row, as the board reads it. */
function valueCell(id: BoardId, r: BoardRow, podium = false): string {
  switch (id) {
    case 'dex': return `${full(r.value)}<span class="lb-of"> / ${full(bestiaryTotal())}</span>`;
    case 'best': return `${r.species ? `<span class="lb-mon cview"${isSample(r) ? '' : ` data-cview="best:${esc(r.name)}" role="button" tabindex="0"`} title="${esc(r.name)}’s ${esc(SPECIES[r.species]?.name ?? '')}"><img src="${spriteUrl(r.species, !!r.aura)}" alt="${esc(SPECIES[r.species]?.name ?? '')}"></span>` : ''}${gradeBadge(Math.round(r.value), 'sm')}<b>${r.value.toFixed(1)}</b>`;
    case 'ladder': { const b = r.value >= 1 ? stepBoss(r.value) : null; return `${b ? `<span class="lb-mon" title="${esc(b.name)}"><img src="${spriteUrl(b.id)}" alt="${esc(b.name)}"></span>` : ''}Step ${full(r.value)}`; }
    case 'level': return r.xp === undefined ? full(r.value) : podium ? `<span title="${full(r.xp)} XP">${full(r.value)}</span>` : `<span class="lb-lvx" title="${full(r.xp)} XP" data-act="lb-xp" role="button" tabindex="0">${full(r.value)}${C.showXp ? `<span class="lb-xp">${big(r.xp)} XP</span>` : ''}</span>`;
    default: return full(r.value);
  }
}

/** Who a row is: a trainer (portrait, tag, name) or, on the guild board, a guild. */
function whoCell(id: BoardId, r: BoardRow, me: boolean): string {
  if (id === 'guild') return `<span class="lb-who">${has('icon-guild-crest') ? `<img class="lb-crest" src="${spriteUrl('icon-guild-crest')}" alt="">` : ''}${tagChip(r.tag)}<b>${esc(r.name)}</b>${isSample(r) ? SAMPLE_TAG : ''}${r.sub ? `<span class="lb-sub">${full(r.sub)} ${r.sub === 1 ? 'member' : 'members'}</span>` : ''}</span>`;
  return `<span class="lb-who">${playerIcon({ outfit: r.outfit, comp: r.comp, size: 'md' })}<span class="lb-nm">${tagChip(r.tag)}${isSample(r) ? sampleName(r.name) : playerLink(r.name, `<b>${esc(r.name)}</b>`)}${me ? '<span class="pill">You</span>' : ''}${r.lv && id !== 'level' ? `<span class="lb-lv">Lv ${full(r.lv)}</span>` : ''}</span></span>`;
}

/** The top three of a board as podium cards: icon with companion, name, level and the value. */
function podiumCards(id: BoardId, rows: BoardRow[], myName: string | undefined): string {
  if (id === 'guild') return '';
  const top = rows.filter((r) => r.rank <= 3).slice(0, 3);
  if (!top.length) return '';
  // Second, first, third: the winner stands in the middle.
  const order = top.length === 3 ? [top[1], top[0], top[2]] : top;
  return `<div class="lb-podium">${order.map((r) => `<div class="lb-pod r${r.rank}${r.name === myName ? ' me' : ''}${isSample(r) ? ' smp' : ''}"${isSample(r) ? ' data-act="sample-row"' : ` data-player="${esc(r.name)}"`} tabindex="0">
    <span class="lb-pod-rank">${PODIUM.medals[r.rank - 1] ? esc(PODIUM.medals[r.rank - 1]) : `#${r.rank}`}</span>
    ${playerIcon({ outfit: r.outfit, comp: r.comp, size: 'lg', name: r.name })}
    <span class="lb-pod-name">${tagChip(r.tag)}<b>${esc(r.name)}</b>${isSample(r) ? SAMPLE_TAG : ''}</span>
    ${r.lv && id !== 'level' ? `<span class="lb-pod-lv">Lv ${full(r.lv)}</span>` : ''}
    <span class="lb-pod-val">${valueCell(id, r, true)}</span></div>`).join('')}</div>`;
}

const rankCell = (rank: number) => `<span class="lb-rank${rank <= 3 ? ` r${rank}` : ''}">${full(rank)}</span>`;

/** Weekly Catch: your species this week, the milestones (each paid once a week) and when the week resets. */
function weekPanel(): string {
  const t = S.trainer;
  if (!t) return '';
  const now = Date.now(), n = weekCatchCount(t, now);
  const ends = Date.parse(`${weekOf(now)}T00:00:00Z`) + 7 * 86_400_000;
  const left = Math.max(0, ends - now), d = Math.floor(left / 86_400_000), h = Math.floor((left % 86_400_000) / 3_600_000);
  const steps = WEEK_CATCH.steps.map((s) => {
    const r = stepReward(s, t.level), got = n >= s.at;
    return `<li class="wc-step${got ? ' got' : ''}" title="${full(r.coins)} coins · ${r.seals} ${esc(ITEM[r.seal]?.name ?? r.seal)}s"><b>${s.at}</b><span>${got ? '✓' : `${num(r.coins)} coins · ${r.seals} seals`}</span></li>`;
  }).join('');
  const next = WEEK_CATCH.steps.find((s) => n < s.at);
  return `<section class="wc panel">
    <div class="wc-h"><b>${full(n)}</b><span class="muted">species this week${next ? ` · next at ${next.at}` : ''}</span><span class="spacer"></span><span class="muted">Resets in ${d ? `${d} d ` : ''}${h} h</span></div>
    <ol class="wc-steps">${steps}</ol>
  </section>`;
}

/** Last month's podium for this board (not the Weekly Catch, which has its own) and the Crown: a small strip. */
function podiumStrip(id: BoardId, p: PodiumDTO | undefined): string {
  if (!p || id === 'week') return '';
  const places = p.boards[id] ?? [];
  const who = (n: string) => (id === 'guild' ? `<b>${esc(n)}</b>` : playerLink(n, `<b>${esc(n)}</b>`));
  const list = places.length
    ? places.map((x) => `<span class="pd-place"><span class="pd-medal r${x.rank}" title="${esc(PODIUM.medals[x.rank - 1])}">${x.rank}</span>${who(x.name)}</span>`).join('')
    : '<span class="muted">Nobody placed.</span>';
  const crown = p.crown
    ? `<span class="pd-crown" title="Strongest creature by Power: ${esc(SPECIES[p.crown.species]?.name ?? p.crown.species)}, ${full(p.crown.power)}"><span class="pd-glyph" aria-hidden="true">${PODIUM.badge.glyph}</span>${playerLink(p.crown.name, `<b>${esc(p.crown.name)}</b>`)}<img src="${spriteUrl(p.crown.species)}" alt="${esc(SPECIES[p.crown.species]?.name ?? '')}"></span>`
    : '';
  return `<section class="pd panel" aria-label="Last month's podium"><span class="pd-h">Last month's podium · ${esc(monthLabel(p.month))}</span><span class="pd-list">${list}</span>${crown}</section>`;
}

export function boardsPane(): string {
  const id = boardOf();
  const menu = `<nav class="lb-menu" role="tablist" aria-label="Board">${BOARD_IDS.map((b) => `<button role="tab" class="${b === id ? 'on' : ''}" aria-selected="${b === id}" data-act="board" data-id="${b}" title="${BOARD_NAME[b]}">${uiIcon(BOARD_ICON[b], '', '', 32)}<span>${BOARD_NAME[b]}</span></button>`).join('')}</nav>`;
  const d = withSampleBoard(C.boards[id], id, bestiaryTotal());
  if (!d) return `<div class="lb-wrap">${menu}<div class="lb"><p class="hint">Loading…</p></div></div>`;
  const myName = id === 'guild' ? S.trainer?.guild?.name : S.trainer?.name;
  const isMe = (r: BoardRow) => !!myName && r.name === myName;
  const you = d.you
    ? `<div class="lb-you panel">${rankCell(d.you.rank)}${whoCell(id, d.you, false)}<span class="lb-val">${valueCell(id, d.you)}</span><span class="lb-total">of ${full(d.total)}</span></div>`
    : `<div class="lb-you panel off"><span class="lb-rank">–</span><span class="lb-who">${id === 'guild' && !S.trainer?.guild ? '<a class="link" href="#/guild">Find a guild</a>' : 'Unranked'}</span><span class="lb-total">${full(d.total)} ranked</span></div>`;
  // Rows already standing on the podium are hidden under it where it shows (public/fit.css).
  const onPod = new Set(id === 'guild' ? [] : d.rows.filter((r) => r.rank <= 3).slice(0, 3));
  const rows = d.rows.map((r) => `<tr data-key="lr-${r.rank}-${esc(r.name)}" class="${onPod.has(r) ? 'pod ' : ''}${isMe(r) ? 'me' : ''}${r.rank <= 3 ? ` top${r.rank}` : ''}"><td class="num">${rankCell(r.rank)}</td><td>${whoCell(id, r, isMe(r))}</td><td class="num lb-v">${valueCell(id, r)}</td></tr>`).join('');
  return `<div class="lb-wrap">${menu}<div class="lb"><h4 class="lb-name">${BOARD_NAME[id]}</h4>${id === 'week' ? weekPanel() : ''}${podiumStrip(id, d.podium)}${podiumCards(id, d.rows, myName)}${you}
    <div class="lb-table panel"><table class="board lbt"><thead><tr><th class="num">#</th><th>${id === 'guild' ? 'Guild' : 'Trainer'}</th><th class="num">${VALUE_NAME[id]}</th></tr></thead><tbody data-fit="lb-${id}">
      ${rows || '<tr><td colspan="3" class="hint">Nobody yet.</td></tr>'}
    </tbody></table></div>
    <p class="lb-at">Updated ${new Date(d.at).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })}</p></div></div>`;
}

/** The species page's line for its record: who holds it, the grade and the rolls. Asks for records once if none are loaded. */
let asked = false;
export function speciesRecord(species: string): string {
  if (!C.records) { if (!asked) { asked = true; send({ t: 'records' }); } return ''; }
  const r = C.records.find((x) => x.species === species);
  const me = S.trainer?.name;
  return `<section class="sp-record panel">
    <div class="spr-h"><h4>${has('icon-record-seal') ? `<img class="rec-seal" src="${spriteUrl('icon-record-seal')}" alt="">` : ''}Server record</h4><a class="link" href="#/records/${species}">All records</a></div>
    ${r ? `<div class="spr-row cview" data-cview="record:${species}" tabindex="0" title="View the record ${esc(SPECIES[species]?.name ?? species)}"><img src="${spriteUrl(species, r.rolls.aura)}" alt=""><div class="spr-who">${playerLink(r.name, `<b>${esc(r.name)}</b>`)}${r.name === me ? ' <span class="pill">You</span>' : ''}<span class="muted">${day(r.at)}</span></div>${rollPills(asCreature(r), { rollsOnly: true })}</div>` : '<p class="muted">None yet.</p>'}
    </section>`;
}

export function communityClick(el: HTMLElement): boolean {
  switch (el.dataset.act) {
    case 'community-refresh':
      if (S.page === 'leaderboards') loadCommunity('leaderboards', boardOf(), true); else loadCommunity(S.page);
      return true;
    case 'rec-mine': C.mine = !C.mine; rerender(); return true;
    case 'rec-region': C.region = el.dataset.id ?? ''; rerender(); return true;
    case 'lb-xp': C.showXp = !C.showXp; rerender(); return true;
    case 'board': {
      const id = el.dataset.id;
      if (!isBoardId(id)) return true;
      history.replaceState(null, '', `#/leaderboards/${id}`);
      askBoard(id);
      rerender();
      return true;
    }
    default: return false;
  }
}

export function communityChange(el: HTMLInputElement | HTMLSelectElement): boolean {
  const k = el.dataset.rec;
  if (k === 'q') { C.q = el.value; return true; }
  return false;
}

/** The feed line for a new record: a highlight with the species sprite and a link, never a pop-up. */
export function recordLine(b: { text: string; species?: string }) {
  return `${has('icon-record-seal') ? `<img class="rec-seal" src="${spriteUrl('icon-record-seal')}" alt="" title="Record">` : ''}${b.species ? `<img class="bsp" src="${spriteUrl(b.species)}" alt="">` : ''}<span>${esc(b.text)}</span> <a href="#/records${b.species ? `/${b.species}` : ''}">Records</a>`;
}
